// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from 'react';
import Plot from 'react-plotly.js';

interface RegionalMetrics {
  population: number;
  coverage: number;
  casesPrevented: number;
  hospitalizationsPrevented: number;
  lifeYearsGained: number;
  costImpact: number;
}

interface RegionalHighlightsProps {
  selectedRegion: string;
  regionalMetrics: RegionalMetrics;
  nationalMetrics: RegionalMetrics;
  totalSelectedMetrics?: RegionalMetrics | null;
  timeHorizonYears?: number;
  title?: string;
  availableRegions?: { id: string; name: string }[];
  onRegionChange?: (regionId: string) => void;
}

/**
 * Regional Highlights showing comparison between selected region and national totals
 * Displays KPI grid with region vs national metrics
 */
export const RegionalHighlights: React.FC<RegionalHighlightsProps> = ({
  selectedRegion,
  regionalMetrics,
  nationalMetrics,
  totalSelectedMetrics,
  timeHorizonYears = 1,
  title = 'Regional analysis',
  availableRegions = [],
  onRegionChange,
}) => {
  
  // Visibility state
  const [visibleSeries, setVisibleSeries] = useState({
    regional: true,
    totalSelected: true,
    national: true
  });

  const isTotalSelectedAvailable = !!(totalSelectedMetrics && selectedRegion !== 'Total Selected' && selectedRegion !== 'selected_total');

  // Helper function to calculate annual values
  const getAnnualValue = (total: number, showAnnual: boolean = true): string => {
    if (!showAnnual || timeHorizonYears <= 1) return '';
    const annual = total / timeHorizonYears;
    return annual.toLocaleString('fi-FI', { maximumFractionDigits: 0 });
  };

  // Helper function to format cost with appropriate magnitude
  const formatCostScaled = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
      return `€${(value / 1_000_000).toFixed(1)}M`;
    } else if (absValue >= 1_000) {
      return `€${(value / 1_000).toFixed(1)}K`;
    } else {
      return `€${value.toFixed(1)}`;
    }
  };

  const metrics = [
    {
      label: 'Population',
      regional: regionalMetrics.population.toLocaleString('fi-FI'),
      regionalAnnual: '',
      totalSelected: totalSelectedMetrics?.population.toLocaleString('fi-FI'),
      totalSelectedAnnual: '',
      national: nationalMetrics.population.toLocaleString('fi-FI'),
      nationalAnnual: '',
      unit: '',
      showAnnual: false,
    },
    {
      label: 'Coverage',
      regional: `${regionalMetrics.coverage.toFixed(0)}%`,
      regionalAnnual: '',
      totalSelected: totalSelectedMetrics ? `${totalSelectedMetrics.coverage.toFixed(0)}%` : '',
      totalSelectedAnnual: '',
      national: `${nationalMetrics.coverage.toFixed(0)}%`,
      nationalAnnual: '',
      unit: '',
      showAnnual: false,
    },
    {
      label: 'Cases Prevented',
      regional: regionalMetrics.casesPrevented.toLocaleString('fi-FI', { maximumFractionDigits: 0 }),
      regionalAnnual: getAnnualValue(regionalMetrics.casesPrevented),
      totalSelected: totalSelectedMetrics?.casesPrevented.toLocaleString('fi-FI', { maximumFractionDigits: 0 }),
      totalSelectedAnnual: totalSelectedMetrics ? getAnnualValue(totalSelectedMetrics.casesPrevented) : '',
      national: nationalMetrics.casesPrevented.toLocaleString('fi-FI', { maximumFractionDigits: 0 }),
      nationalAnnual: getAnnualValue(nationalMetrics.casesPrevented),
      unit: '',
      showAnnual: true,
    },
    {
      label: 'Hospitalizations Prevented',
      regional: regionalMetrics.hospitalizationsPrevented.toLocaleString('fi-FI', { maximumFractionDigits: 0 }),
      regionalAnnual: getAnnualValue(regionalMetrics.hospitalizationsPrevented),
      totalSelected: totalSelectedMetrics?.hospitalizationsPrevented.toLocaleString('fi-FI', { maximumFractionDigits: 0 }),
      totalSelectedAnnual: totalSelectedMetrics ? getAnnualValue(totalSelectedMetrics.hospitalizationsPrevented) : '',
      national: nationalMetrics.hospitalizationsPrevented.toLocaleString('fi-FI', { maximumFractionDigits: 0 }),
      nationalAnnual: getAnnualValue(nationalMetrics.hospitalizationsPrevented),
      unit: '',
      showAnnual: true,
    },
    {
      label: 'Life-Years Gained',
      regional: regionalMetrics.lifeYearsGained.toFixed(0),
      regionalAnnual: timeHorizonYears > 1 ? (regionalMetrics.lifeYearsGained / timeHorizonYears).toFixed(1) : '',
      totalSelected: totalSelectedMetrics?.lifeYearsGained.toFixed(0),
      totalSelectedAnnual: totalSelectedMetrics && timeHorizonYears > 1 ? (totalSelectedMetrics.lifeYearsGained / timeHorizonYears).toFixed(1) : '',
      national: nationalMetrics.lifeYearsGained.toFixed(0),
      nationalAnnual: timeHorizonYears > 1 ? (nationalMetrics.lifeYearsGained / timeHorizonYears).toFixed(1) : '',
      unit: '',
      showAnnual: true,
    },
    {
      label: 'Cost Impact',
      regional: `€${(regionalMetrics.costImpact / 1_000_000).toFixed(1)}M`,
      regionalAnnual: timeHorizonYears > 1 ? formatCostScaled(regionalMetrics.costImpact / timeHorizonYears) : '',
      totalSelected: totalSelectedMetrics ? `€${(totalSelectedMetrics.costImpact / 1_000_000).toFixed(1)}M` : '',
      totalSelectedAnnual: totalSelectedMetrics && timeHorizonYears > 1 ? formatCostScaled(totalSelectedMetrics.costImpact / timeHorizonYears) : '',
      national: `€${(nationalMetrics.costImpact / 1_000_000).toFixed(1)}M`,
      nationalAnnual: timeHorizonYears > 1 ? formatCostScaled(nationalMetrics.costImpact / timeHorizonYears) : '',
      unit: '',
      showAnnual: true,
    },
  ];


  // Filter out Population and Coverage from chart (only show outcome metrics)
  const chartMetrics = metrics.filter(m => m.label !== 'Population' && m.label !== 'Coverage');

  const chartData: any[] = [];

  if (visibleSeries.regional) {
    chartData.push({
      x: chartMetrics.map((m) => m.label),
      y: chartMetrics.map((m) => {
        const regionalStr = m.regional.replace(/[€,%M]/g, '').replace(/,/g, '');
        return parseFloat(regionalStr) || 0;
      }),
      name: selectedRegion === 'Total Selected' || selectedRegion === 'selected_total' ? 'Total Selected' : 'Selected Region',
      type: 'bar',
      marker: { color: '#4C8DFF' }, // Standardized blue from Cost Composition
    });
  }

  if (isTotalSelectedAvailable && visibleSeries.totalSelected) {
    chartData.push({
      x: chartMetrics.map((m) => m.label),
      y: chartMetrics.map((m) => {
        const totalStr = m.totalSelected?.replace(/[€,%M]/g, '').replace(/,/g, '') || '0';
        return parseFloat(totalStr) || 0;
      }),
      name: 'Total Selected',
      type: 'bar',
      marker: { color: '#6A5BE8' }, // Standardized purple from Cost Composition
    });
  }

  if (visibleSeries.national) {
    chartData.push({
      x: chartMetrics.map((m) => m.label),
      y: chartMetrics.map((m) => {
        const nationalStr = m.national.replace(/[€,%M]/g, '').replace(/,/g, '');
        return parseFloat(nationalStr) || 0;
      }),
      name: 'National',
      type: 'bar',
      marker: { color: '#34d399' }, // Standardized green/savings color
    });
  }

  // Calculate visible columns for grid
  const visibleColumns = [
    visibleSeries.regional,
    isTotalSelectedAvailable && visibleSeries.totalSelected,
    visibleSeries.national
  ].filter(Boolean).length;

  const gridTemplateColumns = visibleColumns > 0 ? `repeat(${visibleColumns}, 1fr)` : '1fr';

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div className="vaccine-demo__chart-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {title}
          {availableRegions.length > 0 && onRegionChange && (
            <select
              value={availableRegions.find(r => r.name === selectedRegion)?.id || (selectedRegion === 'Total Selected' ? 'selected_total' : '')}
              onChange={(e) => onRegionChange(e.target.value)}
              style={{
                marginLeft: '8px',
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #334155',
                backgroundColor: '#1e293b',
                color: '#e2e8f0',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="selected_total">Total Selected</option>
              {availableRegions.filter(r => r.id !== 'selected_total' && r.id !== 'finland').map(region => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Visibility Toggles */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto', marginRight: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#e2e8f0', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={visibleSeries.regional} 
              onChange={(e) => setVisibleSeries(prev => ({ ...prev, regional: e.target.checked }))}
              style={{ accentColor: '#4C8DFF' }} // Standardized blue
            />
            {selectedRegion === 'Total Selected' || selectedRegion === 'selected_total' ? 'Total Selected' : 'Region'}
          </label>

          {isTotalSelectedAvailable && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#e2e8f0', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={visibleSeries.totalSelected} 
                onChange={(e) => setVisibleSeries(prev => ({ ...prev, totalSelected: e.target.checked }))}
                style={{ accentColor: '#6A5BE8' }} // Standardized purple
              />
              Total Selected
            </label>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#e2e8f0', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={visibleSeries.national} 
              onChange={(e) => setVisibleSeries(prev => ({ ...prev, national: e.target.checked }))}
              style={{ accentColor: '#34d399' }} // Standardized green
            />
            National
          </label>
        </div>

      </div>

      {/* KPI Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="vaccine-demo__card"
            style={{ padding: '16px' }}
          >
            <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '8px' }}>
              {metric.label}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: gridTemplateColumns, gap: '8px', alignItems: 'baseline' }}>
              {visibleSeries.regional && (
                <div>
                  <div style={{ fontSize: '10px', color: '#64748b' }}>
                      {selectedRegion === 'Total Selected' || selectedRegion === 'selected_total' ? 'Total Selected' : 'Region'}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#4C8DFF' }}> {/* Standardized blue */}
                    {metric.regional}
                  </div>
                  {metric.showAnnual && metric.regionalAnnual && (
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      {metric.regionalAnnual} / year
                    </div>
                  )}
                </div>
              )}
              
              {isTotalSelectedAvailable && visibleSeries.totalSelected && (
                <div style={{ textAlign: visibleColumns === 3 ? 'center' : 'left' }}>
                  <div style={{ fontSize: '10px', color: '#64748b' }}>Total Selected</div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#6A5BE8' }}> {/* Standardized purple */}
                    {metric.totalSelected}
                  </div>
                  {metric.showAnnual && metric.totalSelectedAnnual && (
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      {metric.totalSelectedAnnual} / year
                    </div>
                  )}
                </div>
              )}

              {visibleSeries.national && (
                <div style={{ textAlign: visibleColumns > 1 ? 'right' : 'left' }}>
                  <div style={{ fontSize: '10px', color: '#64748b' }}>National</div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#34d399' }}> {/* Standardized green */}
                    {metric.national}
                  </div>
                  {metric.showAnnual && metric.nationalAnnual && (
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      {metric.nationalAnnual} / year
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Comparison Chart */}
      <div className="vaccine-demo__chart">
        <Plot
          data={chartData}
          layout={{
            barmode: 'group',
            autosize: true,
            height: 300,
            margin: { l: 60, r: 20, t: 30, b: 100 },
            xaxis: {
              tickangle: -30,
              tickfont: { size: 10 },
            },
            yaxis: {
              title: { text: 'Value' },
              tickformat: ',.0f',
              gridcolor: '#1f2937',
            },
            legend: {
              orientation: 'h',
              y: 1.1,
            },
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            font: { color: '#e2e8f0' },
          }}
          config={{ responsive: true, displaylogo: false }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

export default RegionalHighlights;
